"use client"

import styled from "@emotion/styled"

import { useTranslations } from "next-intl"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useMemo, useRef, useState } from "react"
import { ArrowDown, BlockRenderer, Container, CustomBreadcrumbs, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import {
  APARTMENT_TAKEN_STATUS,
  extractProperty,
  findProperty,
  getBreadCrumbs,
  useOutsideClick,
} from "utils"
import { GetAllAp } from "utils/priorityApartments"
import ContactForm from "../Contact/ContactForm"
import { ProjectsMessages } from "../Projects/Projects"
import Apartments from "./Apartments"
import ApartmentsBanner from "./ApartmentsBanner"

type Props = {
  data: any
  locale: string
  messages: ProjectsMessages
  allApartments: GetAllAp
  companyId?: number
}

const components = {
  apartmentsBanner: ApartmentsBanner,
}

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 40px;
    align-items: flex-start;
  }
`

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FilterLabel = styled.span`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #000;
`

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`

const Chip = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  border-radius: 8px;
  padding: 0 16px;
  height: 42px;
  font-size: 20px;
  font-weight: ${(props) => (props.$active ? 700 : 400)};
  line-height: ${(props) => (props.$active ? "1.2" : "normal")};
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  border: ${(props) => (props.$active ? "none" : "1px solid #6f6f6f")};
  background-color: ${(props) => (props.$active ? "#2768a3" : "white")};
  color: ${(props) => (props.$active ? "white" : "black")};
  white-space: nowrap;
  transition: background-color 0.2s, color 0.2s;
  &:hover {
    background-color: ${(props) =>
      props.$active ? "#2768a3" : props.$disabled ? "white" : "#f7f7f7"};
  }
`

const DropdownContainer = styled.div`
  position: relative;
  min-width: 335px;
  ${(props) => props.theme.breakpoints.down("md")} {
    min-width: 100%;
  }
`

const DropdownTrigger = styled.div<{ $open?: boolean }>`
  display: flex;
  height: 48px;
  padding: 8px 12px;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border: 1.2px solid black;
  border-radius: ${(props) => (props.$open ? "8px 8px 0 0" : "8px")};
  background: white;
`

const FilterDropdownInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-size: 16px;
  color: black;
  background: transparent;
  text-align: inherit;
  font-weight: 700;
  &::placeholder {
    color: #868a90;
    font-weight: 300;
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 250px;
  z-index: 100;
  overflow-y: auto;
  border-radius: 0 0 8px 8px;
  border: 1px solid black;
  border-top: none;
  background: white;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.16);
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 200px;
    background: black;
    width: 4px;
  }
`

const DropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  padding: 12px 8px;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #f7f7f7;
  background: ${(props) => (props.$selected ? "#f0f6fc" : "white")};
  cursor: pointer;
  font-weight: ${(props) => (props.$selected ? 700 : 400)};
  &:hover {
    background: ${(props) => (props.$selected ? "#f0f6fc" : "#f7f7f7")};
  }
  &:last-child {
    border-bottom: none;
  }
`


function ApartmentsRent({ data, locale, messages, allApartments, companyId }: Props) {
  const { selfObject, parents } = getBreadCrumbs(data, locale)
  const blocks = findProperty(data.page, "blocks")?.blocks || []
  const hasBanner = blocks?.some(
    (block: any) => block.blockType === "topBanner" || block.blockType === "HeaderWithHeroAndText",
  )
  const noAvailBannerBlock = blocks?.find(
    (block: any) => block.blockType === "noAvailableApartmentsBanner",
  )
  const noAvailableApartmentsBanner = noAvailBannerBlock
    ? {
        title: extractProperty(noAvailBannerBlock, "title") as string | undefined,
        text: extractProperty(noAvailBannerBlock, "text") as string | undefined,
        button: (extractProperty(noAvailBannerBlock, "button") || [])?.[0] as
          | { name?: string; url?: string }
          | undefined,
      }
    : undefined
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const t = useTranslations("BuildingInfo")

  const { apartmentsArr: rawApartmentsArr, allApChar, allBuChar, cmsProjectLeadIds } = allApartments
  const apartmentsArr = useMemo(
    () => rawApartmentsArr.filter((ap) => ap.moreInfoContentHTML !== APARTMENT_TAKEN_STATUS),
    [rawApartmentsArr],
  )

  // All project names
  const availableProjectNames = useMemo(
    () =>
      [
        ...new Set(apartmentsArr.map((el) => el.umbracoName).filter((n): n is string => !!n)),
      ].sort(),
    [apartmentsArr],
  )

  // Initial state from URL
  const initialProject = searchParams.get("project") || "all"
  const initialRooms = searchParams.get("rooms")
    ? searchParams.get("rooms")!.split(",").filter(Boolean)
    : ["all"]

  const [selectedProject, setSelectedProject] = useState<string>(initialProject)
  const [selectedRooms, setSelectedRooms] = useState<string[]>(initialRooms)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [projectSearch, setProjectSearch] = useState("")
  const filteredProjectNames = availableProjectNames.filter((name) =>
    name.toLowerCase().includes(projectSearch.toLowerCase()),
  )
  const dropdownRef = useRef(null)
  useOutsideClick([dropdownRef], () => {
    setDropdownOpen(false)
    setProjectSearch("")
  })

  const projectRef = useRef(selectedProject)
  const roomsRef = useRef(selectedRooms)
  projectRef.current = selectedProject
  roomsRef.current = selectedRooms

  // Update URL params
  const updateUrlParams = useCallback(
    (overrides: Partial<Record<"project" | "rooms", string | string[]>>) => {
      const project = (overrides.project as string) ?? projectRef.current
      const rooms = (overrides.rooms as string[]) ?? roomsRef.current
      const params = new URLSearchParams()
      if (project !== "all") params.set("project", project)
      if (!rooms.includes("all") && rooms.length > 0) params.set("rooms", rooms.join(","))
      const qs = params.toString()
      router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false })
    },
    [pathname, router],
  )

  // Apartments filtered by selected project
  const projectFilteredApartments = useMemo(() => {
    if (selectedProject === "all") return apartmentsArr
    return apartmentsArr.filter((el) => el.umbracoName === selectedProject)
  }, [apartmentsArr, selectedProject])

  // Fixed room values per ticket ASH-1292
  const allRoomsInProject = ["2", "2.5", "3", "3.5", "4", "4.5", "5", "5.5", "6"]

  // Available room counts (rooms that have apartments with entry dates)
  const availableRooms = useMemo(() => {
    return new Set(
      projectFilteredApartments.map((el) => el.numberOfRooms).filter((r): r is string => !!r),
    )
  }, [projectFilteredApartments])

  // Handle project change (single-select) — preserve room selection
  const handleProjectChange = useCallback(
    (value: string) => {
      setSelectedProject(value)
      updateUrlParams({ project: value })
    },
    [updateUrlParams],
  )

  // Handle room chip click
  const handleRoomToggle = useCallback(
    (room: string) => {
      setSelectedRooms((prev) => {
        let next: string[]
        if (room === "all") {
          next = ["all"]
        } else {
          const without = prev.filter((r) => r !== "all" && r !== room)
          if (prev.includes(room)) {
            next = without.length > 0 ? without : ["all"]
          } else {
            next = [...without, room]
          }
        }
        updateUrlParams({ rooms: next })
        return next
      })
    },
    [updateUrlParams],
  )

  // Reset all filters
  const handleResetAllFilters = useCallback(() => {
    setSelectedProject("all")
    setSelectedRooms(["all"])
    projectRef.current = "all"
    roomsRef.current = ["all"]
    updateUrlParams({ project: "all", rooms: ["all"] })
  }, [updateUrlParams])

  return (
    <>
      <Container>
        <CustomBreadcrumbs current={selfObject} parents={parents} />
      </Container>
      {blocks?.map((block: any, index: number) => {
        let additionalProps = {}
        if (block.blockType === "contentText") {
          additionalProps = {
            hasBanner,
          }
        }
        return (
          <BlockRenderer
            key={index}
            blockType={block.blockType}
            blockData={block}
            components={components}
            props={additionalProps}
          />
        )
      })}
      <Container>
        <FiltersContainer>
          {/* Project dropdown (right in RTL) */}
          <FilterSection>
            <FilterLabel>{t("projects")}</FilterLabel>
            <DropdownContainer ref={dropdownRef}>
              <DropdownTrigger $open={dropdownOpen} onClick={() => setDropdownOpen(true)}>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                  <FilterDropdownInput
                    value={
                      dropdownOpen
                        ? projectSearch
                        : selectedProject === "all"
                        ? ""
                        : selectedProject
                    }
                    onChange={(e) => {
                      setProjectSearch(e.target.value)
                      if (!dropdownOpen) setDropdownOpen(true)
                    }}
                    onFocus={() => setDropdownOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setDropdownOpen(false)
                    }}
                    placeholder={t("selectProject")}
                  />
                </div>
                <ArrowDown
                  stroke={sharedColors.black}
                  style={{
                    transform: dropdownOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s",
                  }}
                />
              </DropdownTrigger>
              {dropdownOpen && (
                <DropdownMenu>
                  {filteredProjectNames.map((name) => (
                    <DropdownItem
                      key={name}
                      $selected={selectedProject === name}
                      onClick={() => {
                        handleProjectChange(name === selectedProject ? "all" : name)
                        setDropdownOpen(false)
                        setProjectSearch("")
                      }}
                    >
                      <NewText component="span" desktopFontSize={16} color={sharedColors.black}>
                        {name}
                      </NewText>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              )}
            </DropdownContainer>
          </FilterSection>
          {/* Room chips (left in RTL) */}
          <FilterSection>
            <FilterLabel>{t("roomCount")}</FilterLabel>
            <ChipsRow>
              <Chip $active={selectedRooms.includes("all")} onClick={() => handleRoomToggle("all")}>
                {t("allRooms")}
              </Chip>
              {allRoomsInProject.map((room) => {
                const isAvailable = availableRooms.has(room)
                const isSelected = selectedRooms.includes(room)
                return (
                  <Chip
                    key={room}
                    $active={isSelected}
                    $disabled={!isAvailable}
                    onClick={() => handleRoomToggle(room)}
                  >
                    {room}
                  </Chip>
                )
              })}
            </ChipsRow>
          </FilterSection>
        </FiltersContainer>
      </Container>
      <Apartments
        allApChar={allApChar}
        allBuChar={allBuChar}
        selectedFilters={["all"]}
        selectedRooms={selectedRooms}
        selectedProjects={selectedProject === "all" ? ["all"] : [selectedProject]}
        onResetAllFilters={handleResetAllFilters}
        allApartments={apartmentsArr}
        pageData={data}
        messages={messages}
        locale={locale}
        companyId={companyId}
        noAvailableApartmentsBanner={noAvailableApartmentsBanner}
        cmsProjectLeadIds={cmsProjectLeadIds}
      />
      {blocks
        ?.filter((block: any) => block.blockType === "contactHeader11")
        ?.map((block: any, index: number) => {
          return (
            <ContactForm
              key={index}
              data={block}
              companyId={companyId?.toString()}
              messages={messages}
              companyName={"Ashtrom Residential for rent"}
            />
          )
        })}
    </>
  )
}

export default ApartmentsRent
